import { useEffect, Suspense, useState, useRef } from 'react';
import { Route, Switch, BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { ScrollToTop } from 'modules/routing';
import VerticalLayout from 'modules/layouts/Vertical';
import { authProtectedFlattenRoutes, noLayoutFlattenRoutes } from './index';
import { useTheme } from 'modules/media/theme';
import ReferralIdObserver from 'modules/routing/ReferralIdObserver';
import { useExpandTelegramApp } from 'modules/telegram/core/useExpandTelegramApp';
import { getLocalAccessToken } from 'modules/user/userCookies';
import {
  getAccessTokenRefreshDelay,
  startSilentRefresh,
} from 'modules/user/silentRefresh';
import { useWebsocketConnect } from 'api/services/websocket/useWebsocketConnect';
import { UserTwoFactor, useUserState } from 'modules/user/UserContext';
import { useConnectionStatus } from 'modules/routing/ConnectionStatusProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useJustWokeUp } from './isAwakeStatus';

import { LoaderDex } from '../app/dex/components/alerts/Loader';
import {
  handleAccessTokenRefresh,
  useConnectionError,
  waitUntilTokenIsFresh,
  withTimeout,
} from './tokenRefresh';
import { LOCAL_STORAGE_FORCED_REFRESH } from '../app/dex/side-panels/SnipesPanel';

const RECONNECT_TIMEOUT_MS = 20000; // 20 seconds
const SentryRoute = Sentry.withSentryRouting(Route);
const Routes = (props: any) => {
  const loading = () => (
    <div className="vh-100 justify-center flex items-center">
      <LoaderDex />
    </div>
  );
  const theme = useTheme();
  useExpandTelegramApp();
  const user = useUserState()?.user;
  const { isOnline } = useConnectionStatus();
  const queryClient = useQueryClient();
  const { sendAuth, connect, waitForSocketOpen, disconnect, isConnected } =
    useWebsocketConnect();
  const token = getLocalAccessToken();
  const [isReady, setIsReady] = useState(false);
  const justWokeUp = useJustWokeUp();
  const wasOnline = useRef<boolean>(isOnline);
  const isRefreshingRef = useRef(false);
  const { setCriticalError } = useConnectionError();

  const refreshConnection = async () => {
    if (isRefreshingRef.current) {
      return;
    }

    isRefreshingRef.current = true;

    try {
      if (isConnected) {
        disconnect?.();
      }

      connect?.();

      await withTimeout(waitForSocketOpen(), RECONNECT_TIMEOUT_MS);
      await withTimeout(
        handleAccessTokenRefresh({ sendAuth }),
        RECONNECT_TIMEOUT_MS
      );
      await withTimeout(waitUntilTokenIsFresh(), RECONNECT_TIMEOUT_MS);
      await withTimeout(queryClient.invalidateQueries(), 3000);

      window.localStorage.setItem(LOCAL_STORAGE_FORCED_REFRESH, 'true');
      setCriticalError(false);
    } catch (err) {
      console.error('Error during refresh connection flow:', err);
      setCriticalError(true);
    } finally {
      setIsReady(true);
      isRefreshingRef.current = false;
    }
  };

  useEffect(() => {
    if (
      wasOnline.current !== isOnline &&
      document.visibilityState === 'visible'
    ) {
      // Came back online
      if (isOnline) {
        setIsReady(false);
        void refreshConnection();
      }

      if (!isOnline) {
        disconnect?.(); // You can delay this too if needed
      }

      wasOnline.current = isOnline;
    }
  }, [isOnline]);

  useEffect(() => {
    if (justWokeUp) {
      setIsReady(false);
      void refreshConnection();
    }
  }, [justWokeUp]);

  useEffect(() => {
    setIsReady(false);
    void refreshConnection();
  }, []);

  useEffect(() => {
    const run = async () => {
      if (token) {
        const refreshTime = getAccessTokenRefreshDelay(token, 5);

        if (refreshTime <= 0) {
          try {
            if (isConnected) {
              disconnect?.();
            }

            connect?.();

            await withTimeout(waitForSocketOpen(), RECONNECT_TIMEOUT_MS);
            await withTimeout(
              handleAccessTokenRefresh({ sendAuth }),
              RECONNECT_TIMEOUT_MS
            );
          } catch (err) {
            console.error('Silent refresh failed at startup', err);
          }
        }

        startSilentRefresh(token, () => {
          setTimeout(() => {
            sendAuth();
          }, 100);
        });
      }
    };
    setTimeout(() => {
      void run();
    }, 100);
  }, [sendAuth, token]);

  // theme logic
  useEffect(() => {
    if (theme !== undefined) {
      document.body.setAttribute('theme', theme);
      if (theme === 'dark') {
        document.body.classList.add('dark');
      } else if (theme === 'light') {
        document.body.classList.remove('dark');
      }
    }
  }, [theme]);

  // show loading screen while token refresh is in progress
  if (!isReady || (!token && !!user && !(user as UserTwoFactor).twoFactor)) {
    return loading();
  }

  const modalRoutes = authProtectedFlattenRoutes.reduce((acc, route) => {
    return [
      ...acc,
      ...(route.subroutes
        ?.filter((subroute: any) => subroute.config?.redirect)
        .map((subroute: any) => subroute.path) || []),
    ];
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop modalRoutes={modalRoutes} />
      <ReferralIdObserver />
      <Switch>
        <SentryRoute path={authProtectedFlattenRoutes.map((r: any) => r.path)}>
          <VerticalLayout {...props}>
            <Switch>
              {authProtectedFlattenRoutes.map((route) => {
                if (route.children) return null;

                if (route.subroutes) {
                  return (
                    <route.route key={route.path} path={route.path}>
                      <>
                        <route.component />
                        {route.subroutes.map((subroute: any) => (
                          <subroute.route
                            key={subroute.path}
                            path={subroute.path}
                            exact={subroute.exact}
                          >
                            <subroute.component {...subroute.config} />
                          </subroute.route>
                        ))}
                      </>
                    </route.route>
                  );
                }

                return (
                  <route.route
                    key={route.path}
                    path={route.path}
                    exact={route.exact}
                    component={route.component}
                  />
                );
              })}
            </Switch>
          </VerticalLayout>
        </SentryRoute>

        <SentryRoute path={noLayoutFlattenRoutes.map((r: any) => r.path)}>
          <Suspense fallback={loading()}>
            <Switch>
              {noLayoutFlattenRoutes.map((route) => {
                return (
                  !route.children && (
                    <route.route
                      key={route.path}
                      path={route.path}
                      roles={route.roles}
                      exact={route.exact}
                      component={route.component}
                    />
                  )
                );
              })}
            </Switch>
          </Suspense>
        </SentryRoute>
      </Switch>
    </BrowserRouter>
  );
};

export default Routes;
